import { lazy } from 'react'
import { Navigate } from 'react-router-dom'
import type { RouteObject } from 'react-router-dom'

const FrameworksPage = lazy(() =>
  import('../pages/frameworks').then((m) => ({ default: m.FrameworksPage }))
)
const FrameworkDetailPage = lazy(() =>
  import('../pages/frameworks').then((m) => ({ default: m.FrameworkDetailPage }))
)
const FrameworkControlsPage = lazy(() =>
  import('../pages/frameworks').then((m) => ({ default: m.FrameworkControlsPage }))
)

/**
 * SPA route aliases — /frameworks/* → /framework-engine/*
 *
 * Shard 227 detected that 18/30 routes requested under /frameworks/* did not
 * exist in the router and silently fell back to the SPA catch-all.
 *
 * We add explicit <Navigate replace> aliases so callers (E2E shards, deep
 * links, nav items) hitting /frameworks/<word> are redirected to the real
 * surface under /framework-engine/*. Static aliases MUST precede the dynamic
 * ':code' segment so they take precedence in React Router matching.
 *
 * Mapping table (kept in sync with e2e/shard227-3-modules-sweep.spec.ts):
 *   catalog        → /framework-engine/catalog
 *   mapping        → /framework-engine/wizard
 *   details        → /framework-engine/catalog  (no per-framework "details" landing)
 *   evidence       → /framework-engine/documents
 *   coverage       → /framework-engine/respond
 *   benchmarking   → /framework-engine/templates
 *   roadmap        → /framework-engine/updates
 *   gaps           → /framework-engine/respond
 *   implementation → /framework-engine/implementation-guide/default
 *   settings       → /framework-engine                (portfolio = settings hub)
 */
const FRAMEWORK_ALIASES: Record<string, string> = {
  catalog: '/framework-engine/catalog',
  mapping: '/framework-engine/wizard',
  details: '/framework-engine/catalog',
  evidence: '/framework-engine/documents',
  coverage: '/framework-engine/respond',
  benchmarking: '/framework-engine/templates',
  roadmap: '/framework-engine/updates',
  gaps: '/framework-engine/respond',
  implementation: '/framework-engine/implementation-guide/default',
  settings: '/framework-engine',
}

export const frameworksRoutes: RouteObject[] = [
  {
    path: '/frameworks',
    children: [
      { index: true, element: <FrameworksPage /> },
      // Static aliases MUST come before the dynamic ':code' route so
      // "/frameworks/catalog" doesn't get treated as a framework code.
      ...Object.entries(FRAMEWORK_ALIASES).map(([slug, target]) => ({
        path: slug,
        element: <Navigate to={target} replace />,
      })),
      { path: ':code', element: <FrameworkDetailPage /> },
      { path: ':code/controls', element: <FrameworkControlsPage /> },
    ],
  },
]
